import React, { useLayoutEffect, useRef, useState } from 'react';
import FilterList from './Drawer';

// The phone/tablet filter card's open/close slide. The price card animates its own height
// (so its rounded bottom stays whole) while the filters it hides fade; home type folds away too.
const FILTER_OPEN_MS = 380;
const FILTER_CLOSE_MS = 460;
// Ease in and out: no sudden start or stop. FILTER_SLIDE_EASE approximates the same curve for
// the scroll that runs alongside the opening.
const FILTER_SLIDE_EASING = 'cubic-bezier(0.45, 0, 0.25, 1)';
const FILTER_SLIDE_EASE = (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);
const prefersReducedMotion = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);
const findScrollParent = (element) => {
    let node = element?.parentElement;
    while (node && node !== document.body) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
        node = node.parentElement;
    }
    return null;
};
const getFilterParts = (column) => {
    const content = column?.querySelector('.drawer-content');
    const area = column?.querySelector('.drawer-section--sliders');
    const price = column?.querySelector('.drawer-section--price');
    if (!content || !area || !price) return null;
    return {
        content,
        area,
        price,
        extras: [...price.querySelectorAll('.drawer-select-box-wrapper > :not(.price-range), .price-range--subtle')],
    };
};
const SLIDE_STYLE_PROPS = ['overflow', 'boxSizing'];
const prepareSlide = (element) => {
    element.style.overflow = 'hidden';
    element.style.boxSizing = 'border-box';
};
const clearSlideStyles = (parts) => {
    if (!parts) return;
    [parts.price].forEach((element) => {
        if (element) SLIDE_STYLE_PROPS.forEach((prop) => { element.style[prop] = ''; });
    });
};
// Scrolls by dy over the slide's duration with the same easing (a smooth scrollIntoView can lag
// behind a height change, so the two would drift apart). A touch on the page stops it.
const glideScrollBy = (scroller, dy, duration) => {
    if (Math.abs(dy) < 1) return;
    const target = scroller || document.scrollingElement || document.documentElement;
    const start = target.scrollTop;
    const startedAt = performance.now();
    let stopped = false;
    const stop = () => { stopped = true; };
    window.addEventListener('touchstart', stop, { once: true, passive: true });
    window.addEventListener('wheel', stop, { once: true, passive: true });
    const step = (now) => {
        if (stopped) return;
        const t = Math.min(1, (now - startedAt) / duration);
        target.scrollTop = start + dy * FILTER_SLIDE_EASE(t);
        if (t < 1) window.requestAnimationFrame(step);
        else {
            window.removeEventListener('touchstart', stop);
            window.removeEventListener('wheel', stop);
        }
    };
    window.requestAnimationFrame(step);
};
// Room at the bottom of the page while the filter closes. Near the end of the page the shrinking
// card would otherwise make the page too short for where it's scrolled, and the browser would pull
// everything (map, card) down. Afterwards it keeps only what holds the current scroll position and
// gives that back as the page is scrolled up, so the view never jumps.
const createScrollSlack = (scroller) => {
    if (!scroller) return null;
    // A spacer after the last listing. Padding or a spacer on the page itself wouldn't do: the
    // listing column is a flex item that shrinks, so the page would absorb it.
    const host = scroller.querySelector('.list-view-feed-column') || scroller;
    const spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.cssText = 'flex:0 0 auto;height:0;pointer-events:none';
    host.appendChild(spacer);
    let extra = 0;
    const apply = (px) => {
        extra = Math.max(0, Math.round(px));
        spacer.style.height = `${extra}px`;
        if (!extra) {
            scroller.removeEventListener('scroll', trim);
            spacer.remove();
        }
    };
    const trim = () => {
        const contentBottom = scroller.scrollHeight - extra;
        apply(scroller.scrollTop + scroller.clientHeight - contentBottom);
    };
    return {
        add: (px) => apply(extra + px),
        settle: () => {
            trim();
            if (extra) scroller.addEventListener('scroll', trim, { passive: true });
        },
    };
};
// Scroll anchoring would move the page while the card changes height; it's off while it slides.
const pauseScrollAnchoring = (scroller) => {
    const target = scroller || document.scrollingElement || document.documentElement;
    const previous = target.style.overflowAnchor;
    target.style.overflowAnchor = 'none';
    return () => { target.style.overflowAnchor = previous; };
};

// Listvy's filter column. On phones and tablets (≤ 1200px) it folds to the area filter and price,
// with a Fler filter button that slides the rest open. Its open state lives here, not in
// ListViewPage, so toggling it re-renders only the filters.
const ListViewFilterColumn = ({
    languageState,
    checkedState,
    handleChange,
    setCustomFilterActive,
    setCustomFilter,
    setKey,
    setListingFilters,
    getPriceHistogram,
    priceHistogramScope,
    onBeforeApply,
}) => {
    const [mobileFilterExpanded, setMobileFilterExpanded] = useState(false);
    const columnRef = useRef(null);
    // The running slide: { animations, parts, closing, release }, or while an opening waits for
    // React to show the filters: { pending: { priceHeight, scroller } }.
    const slideRef = useRef(null);

    const stopSlide = () => {
        const slide = slideRef.current;
        slideRef.current = null;
        slide?.animations?.forEach((animation) => animation.cancel());
        clearSlideStyles(slide?.parts);
        slide?.release?.();
        return slide;
    };

    const toggle = () => {
        const column = columnRef.current;
        const running = stopSlide();
        if (running?.closing) return; // Tapped again while closing: stay open.
        const parts = getFilterParts(column);
        if (!parts || prefersReducedMotion()) {
            setMobileFilterExpanded((prev) => !prev);
            return;
        }
        const scroller = findScrollParent(column);
        if (!mobileFilterExpanded) {
            // Opening: React shows the rest, the layout effect slides it open.
            slideRef.current = {
                pending: {
                    priceHeight: parts.price.getBoundingClientRect().height,
                    scroller,
                },
            };
            setMobileFilterExpanded(true);
            return;
        }

        // Closing: only the filter box shrinks, nothing scrolls. Everything stays rendered while it
        // slides; React hides it when it's done.
        const release = pauseScrollAnchoring(scroller);
        const slack = createScrollSlack(scroller);
        slack?.add(parts.content.getBoundingClientRect().height);
        const priceFrom = parts.price.getBoundingClientRect().height;
        column.classList.replace('is-mobile-expanded', 'is-mobile-collapsed');
        const priceTo = parts.price.getBoundingClientRect().height;
        column.classList.replace('is-mobile-collapsed', 'is-mobile-expanded');
        const options = { duration: FILTER_CLOSE_MS, easing: FILTER_SLIDE_EASING, fill: 'forwards' };
        const animations = [];

        prepareSlide(parts.price);
        animations.push(parts.price.animate(
            [{ height: `${priceFrom}px` }, { height: `${priceTo}px` }],
            options,
        ));
        parts.extras.forEach((element) => {
            animations.push(element.animate(
                [{ opacity: 1 }, { opacity: 0, offset: 0.4 }, { opacity: 0 }],
                options,
            ));
        });
        const slide = {
            animations,
            parts,
            closing: true,
            release: () => {
                release();
                slack?.settle();
            },
        };
        slideRef.current = slide;
        animations[animations.length - 1].onfinish = () => {
            if (slideRef.current !== slide) return;
            // The last frame holds (fill) until the layout effect has hidden the filters.
            slide.closing = false;
            setMobileFilterExpanded(false);
        };
    };

    useLayoutEffect(() => {
        const column = columnRef.current;
        if (!column) return;
        if (!mobileFilterExpanded) {
            stopSlide();
            return;
        }
        const pending = slideRef.current?.pending;
        if (!pending) return;
        slideRef.current = null;
        const parts = getFilterParts(column);
        if (!parts) return;
        // Slide the card open and scroll so the area filter ends at the top of the screen.
        const release = pauseScrollAnchoring(pending.scroller);
        const priceTo = parts.price.getBoundingClientRect().height;
        const options = { duration: FILTER_OPEN_MS, easing: FILTER_SLIDE_EASING };
        prepareSlide(parts.price);
        const animations = [
            parts.price.animate([{ height: `${pending.priceHeight}px` }, { height: `${priceTo}px` }], options),
            ...parts.extras.map((element) => element.animate(
                [{ opacity: 0 }, { opacity: 0, offset: 0.25 }, { opacity: 1 }],
                options,
            )),
        ];
        const slide = { animations, parts, release };
        slideRef.current = slide;
        animations[0].onfinish = () => {
            if (slideRef.current !== slide) return;
            slideRef.current = null;
            clearSlideStyles(parts);
            release();
        };
        const scrollerTop = pending.scroller ? pending.scroller.getBoundingClientRect().top : 0;
        glideScrollBy(pending.scroller, parts.area.getBoundingClientRect().top - scrollerTop - 8, FILTER_OPEN_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileFilterExpanded]);

    return (
        <aside
            ref={columnRef}
            className={`list-view-filter-column${mobileFilterExpanded ? ' is-mobile-expanded' : ' is-mobile-collapsed'}`}
        >
            <div className="list-view-mobile-filter-body">
                <div className="list-view-sticky-shell list-view-filter-shell">
                    <FilterList
                        languageState={languageState}
                        checkedState={checkedState}
                        handleChange={handleChange}
                        setDrawerTab={() => {}}
                        setOpen={() => setMobileFilterExpanded(false)}
                        open
                        setCustomFilterActive={setCustomFilterActive}
                        setCustomFilter={setCustomFilter}
                        setKey={setKey}
                        setListingFilters={setListingFilters}
                        onBeforeApply={onBeforeApply}
                        getPriceHistogram={getPriceHistogram}
                        priceHistogramScope={priceHistogramScope}
                        showMapSwitches={false}
                        commuteIntroTip="interact"
                        expandToggle={(
                            <button
                                type="button"
                                className="list-view-mobile-filter-toggle"
                                onClick={toggle}
                                aria-expanded={mobileFilterExpanded}
                            >
                                {mobileFilterExpanded
                                    ? (languageState.swedish ? 'Färre filter' : 'Fewer filters')
                                    : (languageState.swedish ? 'Fler filter' : 'More filters')}
                                <span
                                    className={`list-view-mobile-filter-toggle-arrow${mobileFilterExpanded ? ' is-open' : ''}`}
                                    aria-hidden="true"
                                >
                                    ▾
                                </span>
                            </button>
                        )}
                    />
                </div>
            </div>
        </aside>
    );
};

export default ListViewFilterColumn;
